import { commonQuestionData, QuestionDataById } from '@src/app-components/dynamic-form/types';
import { getParentObject } from './permitProcessGuideData.types';
import PermitProcessGuideSuggest, { getResultText } from '../PermitProcessGuideSuggest';
import { RequestType } from '@src/utils/enums';

const basePath = import.meta.env.VITE_URL_BASE_PATH;

const additionalInformation = {
  contactDistrict:
    'Please contact your [USACE Regulatory District Office](https://regulatory.ops.usace.army.mil/offices/) for additional assistance',
  jurisdictionalDetermination: `Please visit our [Jurisdiction Information Page](${basePath}/home/jurisdiction) to learn more about Waters of the United States and Jurisdictional Determination options`,
  jpa: `Please review our [Joint Permit Application Process information](${basePath}/home/permitting#jpa-id) or reach out to your [USACE Regulatory District Office](https://regulatory.ops.usace.army.mil/offices/) for additional assistance`,
  permitting: `Please visit our [Permitting Information Page](${basePath}/home/permitting) to learn more about permitting requirements and available permit types`,
  submission:
    "Please follow your District's instructions on how to submit an application package. Contact your [USACE Regulatory District Office](https://regulatory.ops.usace.army.mil/offices/) for additional assistance",
};

const notRequiredQuestionData = {
  isRequired: false,
  showOptionalText: false,
};

export const permitProcessGuideQuestionsById: QuestionDataById = {
  1: {
    sectionId: 1,
    helpInfo: `Aquatic Resources include wetlands, rivers, oceans, streams, lakes, ponds, ditches, and other features which hold or convey water. Only certain aquatic resources are considered "Waters of the United States" and are subject to federal jurisdiction under Section 404 of the Clean Water Act and/or Section 10 of the Rivers and Harbors Act.
      A delineation concurrence will provide confirmation of the extent of aquatic resources on your property without any determination of jurisdiction. A jurisdictional determination will indicate which waters qualify as "Waters of the United States" and are regulated by the USACE.`,
    question:
      'Would you like the USACE to verify the extent and/or jurisdictional status of any aquatic resources on your property?',
    questionTypeId: 7,
    lists: [
      {
        listId: 1,
        valueDesc: 'Yes',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: 'No',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: "I'm not sure",
        sortOrder: 1.0,
        activeInd: true,
      },
    ],
    endConditions: [
      {
        value: "I'm not sure",
        comparison: '=',
        content: <PermitProcessGuideSuggest result={additionalInformation.jurisdictionalDetermination} />,
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
  2: {
    sectionId: 1,
    helpInfo: `A delineation of aquatic resources will identify and map the extent of any potentially regulated waters or wetlands on your project site. A delineation of aquatic resources is a required component of a permit application. Most applicants elect to retain an environmental consultant to prepare the aquatic resource delineation report, which  typically includes maps, datasheets, photographs, and/or figures that depict the location and extent of aquatic resources on the site and describes the methodology used to conduct the delineation.
      A delineation concurrence will provide confirmation of the extent of aquatic resources on your property without any determination of jurisdiction. A jurisdictional determination will indicate which waters qualify as "Waters of the U.S." and are regulated by the USACE. `,
    question: 'Have you conducted an Aquatic Resource Delineation?',
    questionTypeId: 5,
    parentQuestions: [getParentObject(1, 7, '=', 'YES')],
    endConditions: [
      {
        value: 'YES',
        comparison: '=',
        content: (
          <PermitProcessGuideSuggest
            result={`${getResultText(RequestType.AquaticResourceDelineationReportAndInventory)} to upload your delineation information and request delineation concurrence. Select the ${RequestType.JurisdictionalDetermination} request type as well if you would like the USACE to make a determination on the jurisdictional status of resources in your project area`}
          />
        ),
      },
      {
        value: 'NO',
        comparison: '=',
        content: <PermitProcessGuideSuggest result={RequestType.JurisdictionalDetermination} />,
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
  3: {
    sectionId: 1,
    helpInfo: `Under Section 10 of the Rivers and Harbors Act, a USACE permit is required for work or structures in, over, or under navigable waters of the United States. Navigable waters typically include larger rivers, larger freshwater lakes, tidal waters, and the ocean.
      Under Section 404 of the Clean Water Act, a USACE permit is required for the discharge of dredged or fill material into waters of the United States. Waters of the United States include the larger navigable waters of the United States and certain smaller waterbodies such as streams, ponds, and wetlands.`,
    question: 'Does your project require a permit?',
    questionTypeId: 7,
    lists: [
      {
        listId: 1,
        valueDesc: 'Yes',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: 'No',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: "I'm not sure",
        sortOrder: 1.0,
        activeInd: true,
      },
    ],
    endConditions: [
      {
        value: "I'm not sure",
        comparison: '=',
        content: <PermitProcessGuideSuggest result={additionalInformation.permitting} />,
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
  4: {
    sectionId: 1,
    helpInfo: `Joint Permit Application processes streamline permitting requirements in certain geographical areas for projects which require permits from the USACE and other agencies such as states or local governments. ${additionalInformation.jpa}.`,
    question: 'Does your region have a Joint Permit Application (JPA) process?',
    questionTypeId: 7,
    lists: [
      {
        listId: 1,
        valueDesc: 'Yes',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: 'No',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: "I'm not sure",
        sortOrder: 1.0,
        activeInd: true,
      },
    ],
    parentQuestions: [getParentObject(3, 7, '=', 'YES')],
    endConditions: [
      {
        value: "I'm not sure",
        comparison: '=',
        content: <PermitProcessGuideSuggest result={additionalInformation.jpa} />,
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
  5: {
    sectionId: 1,
    question: 'Is the JPA process in your region currently integrated in RRS?',
    questionTypeId: 7,
    lists: [
      {
        listId: 1,
        valueDesc: 'Yes',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: 'No',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: "I'm not sure",
        sortOrder: 1.0,
        activeInd: true,
      },
    ],
    parentQuestions: [getParentObject(4, 7, '=', 'YES')],
    endConditions: [
      {
        value: 'NO',
        comparison: '=',
        content: <PermitProcessGuideSuggest result={additionalInformation.submission} />,
      },
      {
        value: "I'm not sure",
        comparison: '=',
        content: <PermitProcessGuideSuggest result={additionalInformation.jpa} />,
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
  6: {
    sectionId: 1,
    helpInfo:
      'There are three types of general permits: Nationwide Permits, Regional General Permits, and Programmatic General Permits. General permits authorize activities that the USACE has determined to be substantially similar in nature (such as utility lines, residential developments, bank stabilization, etc.) and which cause minimal individual and cumulative environmental impacts. For a list of available General Permits in your region, contact your [USACE Regulatory District Office.](https://regulatory.ops.usace.army.mil/offices/)',
    question: 'Does your project qualify for a General Permit?',
    questionTypeId: 7,
    lists: [
      {
        listId: 1,
        valueDesc: 'Yes',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: 'No',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: "I'm not sure",
        sortOrder: 1.0,
        activeInd: true,
      },
    ],
    parentQuestions: [getParentObject(3, 7, '=', 'YES')],
    endConditions: [
      {
        value: "I'm not sure",
        comparison: '=',
        content: <PermitProcessGuideSuggest result={additionalInformation.permitting} />,
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
  7: {
    sectionId: 1,
    helpInfo:
      'In some cases, activities that qualify for general permit authorization can proceed without written confirmation from the USACE, if they adhere to the terms and conditions outlined in the general permit. However, for many general permits, the USACE must review the proposed work and provide written verification of compliance with the general permit before construction on the project can commence. Written notification to the USACE is almost always required when the project has the potential to impact federally listed threatened or endangered species or properties that are included in, or eligible for inclusion in, the National Register of Historic Places.',
    question: 'Does your project require a Preconstruction Notification?',
    questionTypeId: 7,
    lists: [
      {
        listId: 1,
        valueDesc: 'Yes',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: 'No',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: "I'm not sure",
        sortOrder: 1.0,
        activeInd: true,
      },
    ],
    parentQuestions: [getParentObject(6, 7, '=', 'YES')],
    endConditions: [
      {
        value: 'YES',
        comparison: '=',
        content: <PermitProcessGuideSuggest result={RequestType.PermitApplication} />,
      },
      {
        value: 'NO',
        comparison: '=',
        content: <PermitProcessGuideSuggest result={RequestType.NonPreconstructionNotification} />,
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
  8: {
    sectionId: 1,
    helpInfo:
      'For a list of current Nationwide Permits, visit the [Nationwide Permit Information Page.](https://www.usace.army.mil/Missions/Civil-Works/Regulatory-Program-and-Permits/Nationwide-Permits/)',
    question:
      'Are you proposing to use NWP 3b, 7, 8, 17, 21, 27, 29, 31, 34, 37, 38, 39, 40, 42, 44, 45, 46, 49, 50, 52, 53, 54, 55, or 59?',
    questionTypeId: 5,
    parentQuestions: [getParentObject(7, 7, '=', "I'm not sure")],
    endConditions: [
      {
        value: 'YES',
        comparison: '=',
        content: (
          <PermitProcessGuideSuggest
            result={`NWPs 3b, 7, 8, 17, 21, 27, 29, 31, 32, 34, 37, 38, 39, 40, 42, 44, 45, 46, 49, 50, 52, 53, 54, 55, 59 require a PCN for all activities. ${getResultText(RequestType.PermitApplication)}`}
          />
        ),
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
  9: {
    sectionId: 1,
    question: 'Are you proposing to use a NWP other than those listed in the previous question?',
    questionTypeId: 5,
    parentQuestions: [getParentObject(8, 5, '=', 'NO')],
    endConditions: [
      {
        value: 'YES',
        comparison: '=',
        content: (
          <PermitProcessGuideSuggest
            result={`The NWP you are proposing to use has specific PCN requirements. Please create a new request and select the **${RequestType.NonPreconstructionNotification}** Request Type to determine if your project requires a PCN. If it does, you will be automatically directed to the ${RequestType.PermitApplication} Request Type`}
          />
        ),
      },
      {
        value: 'NO',
        comparison: '=',
        content: <PermitProcessGuideSuggest result={RequestType.PermitApplication} />,
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
  10: {
    sectionId: 1,
    helpInfo: `There are two types of Individual Permits: Standard Permits and Letters of Permissions. A Standard Permit review involves a public notice period of 15 to 30 days, during which the permit drawings and detailed project description are provided to interested parties, neighboring property owners, and State and Federal agencies for review and feedback. A Letter of Permission is an expedited process for individual permits for certain categories of activities.
      First-time applicants are encouraged to request a Pre-application Meeting prior to submitting a full application. An Individual Permit application packet includes the name, address, and signature of the applicant; the project location; a complete description of the proposed activity, including its intended purpose and use; proposed impacts to aquatic resources, including amount and type of material being discharged or dredged; a description of avoidance, minimization, and compensation for impacts; the project schedule; the location and dimensions of adjacent structures; names and addresses of adjoining property owners; a list of authorizations required by other agencies; information on any endangered species and historic properties in the project area; drawings, sketches, or plans of the proposed work; and a delineation of aquatic resources in the project area.`,
    question: 'Are you ready to submit your Individual Permit application?',
    questionTypeId: 7,
    lists: [
      {
        listId: 1,
        valueDesc: 'Yes',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: 'No',
        sortOrder: 1.0,
        activeInd: true,
      },
      {
        listId: 1,
        valueDesc: "I'm not sure",
        sortOrder: 1.0,
        activeInd: true,
      },
    ],
    parentQuestions: [getParentObject(6, 7, '=', 'NO')],
    endConditions: [
      {
        value: 'YES',
        comparison: '=',
        content: <PermitProcessGuideSuggest result={RequestType.PermitApplication} />,
      },
      {
        value: 'NO',
        comparison: '=',
        content: <PermitProcessGuideSuggest result={RequestType.PreApplication} />,
      },
      {
        value: "I'm not sure",
        comparison: '=',
        content: <PermitProcessGuideSuggest result={additionalInformation.permitting} />,
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
  11: {
    sectionId: 1,
    helpInfo:
      'Certain activities in waters of the United States are not regulated, or are exempt under Section 404(f) of the Clean Water Act.  Activities not requiring a permit are described in [33 CFR 323.4.](https://www.ecfr.gov/current/title-33/chapter-II/part-323/section-323.4)',
    question:
      'Do you believe your project does not require a permit from USACE because the activity is not regulated or exempt?',
    questionTypeId: 5,
    parentQuestions: [getParentObject(3, 7, '=', 'NO')],
    endConditions: [
      {
        value: 'YES',
        comparison: '=',
        content: <PermitProcessGuideSuggest result={RequestType.NoPermitRequired} />,
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
  12: {
    sectionId: 1,
    helpInfo:
      'An Approved Jurisdictional Determination (AJD) may be requested for written confirmation that your project site consists only of upland (no aquatic resources) or to determine the jurisdictional status of any aquatic resources present in the review area. You are not required to obtain a permit to place fill material into a non-jurisdictional aquatic resource as determined by an AJD.',
    question:
      'Do you believe your project does not require a permit from USACE because you will not be impacting any jurisdictional waters?',
    questionTypeId: 5,
    parentQuestions: [getParentObject(11, 5, '=', 'NO')],
    endConditions: [
      {
        value: 'YES',
        comparison: '=',
        content: (
          <PermitProcessGuideSuggest
            result={`${getResultText(RequestType.JurisdictionalDetermination)}. If you have completed a delineation, you should select the **${RequestType.AquaticResourceDelineationReportAndInventory}** Request Type as well, in order to upload your delineation information`}
          />
        ),
      },
      {
        value: 'NO',
        comparison: '=',
        content: <PermitProcessGuideSuggest result={additionalInformation.contactDistrict} />,
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
  13: {
    sectionId: 1,
    helpInfo:
      'A standard permit may be modified to account for changes to a proposed project. Some permit modification requests may result in publishing a new Public Notice and new coordination with resource agencies such as the U.S. Fish and Wildlife Service and State Historic Preservation Office.',
    question: 'Would you like to request a modification of an existing Standard Permit?',
    questionTypeId: 5,
    endConditions: [
      {
        value: 'YES',
        comparison: '=',
        content: <PermitProcessGuideSuggest result={RequestType.PermitApplication} />,
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
  14: {
    sectionId: 1,
    helpInfo:
      "If construction has begun or is under contract to begin prior to the expiration of a Nationwide Permit under which the activity is authorized, the project must be completed within one year of the permit's expiration. Otherwise, project proponents may request reverification of their project under a current Nationwide Permit.",
    question:
      'Would you like to request a reverification of a project with existing authorization under a Nationwide Permit or other General Permit?',
    questionTypeId: 5,
    endConditions: [
      {
        value: 'YES',
        comparison: '=',
        content: <PermitProcessGuideSuggest result={RequestType.PermitApplication} />,
      },
    ],
    ...commonQuestionData,
    ...notRequiredQuestionData,
  },
};

export const permitProcessGuideQuestions = [
  permitProcessGuideQuestionsById[1],
  permitProcessGuideQuestionsById[2],
  permitProcessGuideQuestionsById[3],
  permitProcessGuideQuestionsById[4],
  permitProcessGuideQuestionsById[5],
  permitProcessGuideQuestionsById[6],
  permitProcessGuideQuestionsById[7],
  permitProcessGuideQuestionsById[8],
  permitProcessGuideQuestionsById[9],
  permitProcessGuideQuestionsById[10],
  permitProcessGuideQuestionsById[11],
  permitProcessGuideQuestionsById[12],
  permitProcessGuideQuestionsById[13],
  permitProcessGuideQuestionsById[14],
];
